import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TextField,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

import { alarmsService } from '../services/api';

// Etichette tipi allarme
const alarmTypeLabels = {
  delta_position: 'Spostamento',
  quality: 'Qualità GNSS',
  battery: 'Batteria',
  offline: 'Offline',
  velocity: 'Velocità',
};

// Colori severità
const severityColors = {
  critical: '#b71c1c',
  alarm: '#f44336',
  warning: '#ff9800',
  info: '#2196f3',
};

function Alarms() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeAlarms, setActiveAlarms] = useState([]);
  const [alarmHistory, setAlarmHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('active'); // 'active' | 'history'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filterType, setFilterType] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');

  // Carica dati
  const loadData = async () => {
    setLoading(true);
    try {
      const [activeData, historyData, summaryData] = await Promise.all([
        alarmsService.getActive(),
        alarmsService.getHistory({ limit: 100 }),
        alarmsService.getSummary(),
      ]);

      setActiveAlarms(activeData.alarms || []);
      setAlarmHistory(historyData.alarms || []);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading alarms:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handler
  const handleAcknowledge = async (alarmId) => {
    try {
      await alarmsService.acknowledge(alarmId, 'admin');
      loadData();
    } catch (error) {
      console.error('Error acknowledging alarm:', error);
    }
  };

  const handleResolve = async (alarmId) => {
    try {
      await alarmsService.resolve(alarmId);
      loadData();
    } catch (error) {
      console.error('Error resolving alarm:', error);
    }
  };

  // Filtra allarmi
  const filteredAlarms = (view === 'active' ? activeAlarms : alarmHistory).filter((alarm) => {
    if (filterType && alarm.type !== filterType) return false;
    if (filterSeverity && alarm.severity !== filterSeverity) return false;
    return true;
  });

  // Export CSV
  const exportToCSV = () => {
    const headers = ['Data', 'Sensore', 'Tipo', 'Severità', 'Messaggio', 'Valore', 'Soglia', 'Stato'];
    const rows = filteredAlarms.map((a) => [
      format(parseISO(a.created_at), 'yyyy-MM-dd HH:mm:ss'),
      a.sensor_id,
      alarmTypeLabels[a.type] || a.type,
      a.severity,
      a.message,
      a.value || '',
      a.threshold || '',
      a.resolved ? 'Risolto' : a.acknowledged ? 'In gestione' : 'Attivo',
    ]);

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alarms_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pt: 8, height: '100%', overflow: 'auto' }}>
      {/* Header con statistiche */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="error.main" fontWeight="bold">
                {summary?.total_active || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Allarmi attivi
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: severityColors.alarm }} fontWeight="bold">
                {summary?.by_severity?.alarm || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Critici
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: severityColors.warning }} fontWeight="bold">
                {summary?.by_severity?.warning || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Warning
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {alarmHistory.filter((a) => a.resolved).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Risolti (7gg)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Toolbar */}
      <Paper sx={{ p: 2, pt: 8, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, val) => val && setView(val)}
            size="small"
          >
            <ToggleButton value="active">
              Attivi ({activeAlarms.length})
            </ToggleButton>
            <ToggleButton value="history">
              Storico
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField
            select
            size="small"
            label="Tipo"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">Tutti</MenuItem>
            {Object.entries(alarmTypeLabels).map(([key, label]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Severità"
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">Tutte</MenuItem>
            <MenuItem value="critical">Critico</MenuItem>
            <MenuItem value="alarm">Allarme</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="info">Info</MenuItem>
          </TextField>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Aggiorna">
            <IconButton onClick={loadData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Esporta CSV">
            <IconButton onClick={exportToCSV}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Tabella allarmi */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Data/Ora</TableCell>
              <TableCell>Sensore</TableCell>
              {!isMobile && <TableCell>Tipo</TableCell>}
              <TableCell>Severità</TableCell>
              {!isMobile && <TableCell>Messaggio</TableCell>}
              {!isMobile && <TableCell align="right">Valore</TableCell>}
              <TableCell>Stato</TableCell>
              <TableCell align="center">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAlarms
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((alarm) => (
                <TableRow
                  key={alarm.id}
                  sx={{
                    backgroundColor: alarm.severity === 'alarm' || alarm.severity === 'critical'
                      ? '#ffebee'
                      : alarm.severity === 'warning'
                      ? '#fff3e0'
                      : 'inherit',
                  }}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {format(parseISO(alarm.created_at), 'dd/MM/yyyy')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(parseISO(alarm.created_at), 'HH:mm:ss')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {alarm.sensor_name || alarm.sensor_id?.slice(0, 8)}
                    </Typography>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Chip
                        label={alarmTypeLabels[alarm.type] || alarm.type}
                        size="small"
                        sx={{ fontSize: 11 }}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip
                      label={alarm.severity.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: severityColors[alarm.severity],
                        color: 'white',
                        fontSize: 11,
                      }}
                    />
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" noWrap title={alarm.message}>
                        {alarm.message}
                      </Typography>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell align="right">
                      <Typography variant="body2">
                        {alarm.value?.toFixed(3) || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        / {alarm.threshold?.toFixed(3) || '-'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    {alarm.resolved ? (
                      <Chip label="Risolto" size="small" color="success" icon={<CheckIcon />} />
                    ) : alarm.acknowledged ? (
                      <Chip label="In gestione" size="small" color="info" />
                    ) : (
                      <Chip label="Attivo" size="small" color="error" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {!alarm.resolved && (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {!alarm.acknowledged && (
                          <Button
                            size="small"
                            onClick={() => handleAcknowledge(alarm.id)}
                          >
                            ACK
                          </Button>
                        )}
                        <Button
                          size="small"
                          color="success"
                          onClick={() => handleResolve(alarm.id)}
                        >
                          Risolvi
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}

            {filteredAlarms.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                  <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography color="success.main">
                    Nessun allarme trovato
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredAlarms.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Righe per pagina:"
        />
      </TableContainer>
    </Box>
  );
}

export default Alarms;
